import React, { CSSProperties, useMemo, useState } from 'react';
import { Typography, Stack, useMediaQuery, IconButton } from '@mui/material';
import classNames from 'classnames';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from '@/hooks/translations';
import { ITutorial } from '../../repository/types';
import Pagination from '../Pagination';
import styles from './index.module.scss';

interface ISliderItem {
    media: string;
    title?: string;
    description?: string;
}

interface IProps {
    lang: string;
    theme: string;
    tutorial: ITutorial;
    rtl: boolean;
    onDone: (withError?: boolean) => void;
    setOpen: (open: boolean) => void;
    close: () => void;
}

const Slider = ({ lang, theme, tutorial, rtl, onDone, setOpen, close }: IProps) => {
    const { t } = useTranslation();
    const isDesktop = useMediaQuery('(min-width: 429px)');

    const [page, setPage] = useState(0);

    const list = useMemo<ISliderItem[]>(() => {
        try {
            return (
                tutorial.content?.map((item) => ({
                    media:
                        item.mediaCustom
                            ?.find((o) => o?.locale === lang)
                            ?.value?.find((o) => (o.key || 'any') === 'any' || o.key === theme)?.value || item.media,
                    title: item.titleCustom?.find((o) => o?.locale === lang)?.value || item.title,
                    description: item.descriptionCustom?.find((o) => o?.locale === lang)?.value || item.description,
                })) || []
            );
        } catch {
            onDone(true);
            return [];
        }
    }, [tutorial, lang, theme]);

    return (
        <div className={styles.container}>
            <div
                className={classNames(styles.contentContainer, {
                    [styles.contentContainerIsDesktop]: isDesktop,
                })}
            >
                <div className={styles.headerContainer}>
                    <div className={styles.header}>
                        <Typography className={styles.headerTitle}>{t(`What's new here`)}</Typography>
                        <IconButton
                            onClick={() => {
                                setOpen(false);
                                close();
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>
                <div className={styles.imageSliderContainer}>
                    <div
                        className={styles.imageSlider}
                        style={
                            {
                                '--rtl-transform': rtl ? `translateX(${page * 100}%)` : `translateX(-${page * 100}%)`,
                            } as CSSProperties
                        }
                    >
                        {list.map((item, i: number) => (
                            <img key={i} src={item.media} alt={`Slide ${i + 1}`} className={styles.image} />
                        ))}
                    </div>
                </div>

                <div className={styles.descriptionSliderContainer}>
                    <div
                        className={styles.descriptionSlider}
                        style={
                            {
                                '--rtl-transform': rtl ? `translateX(${page * 100}%)` : `translateX(-${page * 100}%)`,
                            } as CSSProperties
                        }
                    >
                        {list.map((item, i) => (
                            <Stack
                                key={i}
                                direction="column"
                                alignItems="flex-start"
                                justifyContent="center"
                                spacing={2}
                                style={{
                                    minWidth: '100%',
                                    paddingLeft: '33px',
                                    paddingRight: '33px',
                                }}
                            >
                                {item.title && <Typography className={styles.title}>{item.title}</Typography>}
                                {item.description && (
                                    <Typography className={styles.description}>{item.description}</Typography>
                                )}
                            </Stack>
                        ))}
                    </div>
                </div>
            </div>

            <Pagination
                totalPages={list.length}
                currentPage={page}
                onPageChange={(val) => {
                    setPage(val);
                }}
                onDone={onDone}
            />
        </div>
    );
};

export default Slider;
